import {
  Button,
  Container,
  Paper,
  Stack,
  TextInput,
  Title,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { fetchApi } from "@/lib/api";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useEffect } from "react";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
});

interface OnboardingFormData {
  companyName: string;
  userName: string;
  email: string;
}

interface OnboardingResponse {
  companyId: string;
  message: string;
}

function OnboardingPage() {
  const navigate = useNavigate();
  const auth = useAuthConditional();
  const { data: onboardingStatus, isLoading: isLoadingStatus } =
    useOnboardingStatus();

  // If user already has a company, redirect to projects
  useEffect(() => {
    if (onboardingStatus?.hasCompany) {
      navigate({ to: "/projects" });
    }
  }, [onboardingStatus, navigate]);

  const form = useForm<OnboardingFormData>({
    initialValues: {
      companyName: "",
      userName: "",
      email: "",
    },
    validate: {
      companyName: (value) =>
        value.trim().length < 2 ? "Company name is required" : null,
      userName: (value) =>
        value.trim().length < 2 ? "Your name is required" : null,
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : "Valid email is required",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: OnboardingFormData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();
      return fetchApi<OnboardingResponse>("/api/onboarding/company", {
        method: "POST",
        headers,
        body: JSON.stringify({
          companyName: data.companyName,
          userName: data.userName,
          email: data.email,
        }),
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "Success",
        message: "Company created successfully!",
        color: "green",
      });
      navigate({ to: "/projects" });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create company",
        color: "red",
      });
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    mutation.mutate(values);
  });

  if (isLoadingStatus) {
    return (
      <Container size="xs" py="xl">
        <Paper p="xl" withBorder>
          <Stack gap="md">
            <Text ta="center">Loading...</Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" py="xl">
      <Paper p="xl" withBorder>
        <Stack gap="md">
          <Title order={2} ta="center">
            Welcome to Kablan
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Let's get your company set up
          </Text>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Company Name"
                placeholder="Enter your company name"
                {...form.getInputProps("companyName")}
                required
              />

              <TextInput
                label="Your Name"
                placeholder="Enter your name"
                {...form.getInputProps("userName")}
                required
              />

              <TextInput
                label="Email"
                placeholder="Enter your email"
                type="email"
                {...form.getInputProps("email")}
                required
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={mutation.isPending}
              >
                Create Company
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
